import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import { MotionConfig } from "framer-motion"
import { useState } from "react"

import {
  getTerminalAuthContext,
  terminalLogout,
} from "@/actions/terminal-auth-actions"
import { IdentityReviewsNavLink } from "@/admin/identity-reviews/nav-link"
import { MonitoringCompanySearch } from "@/admin/monitoring/company-search"
import {
  useWorkerStatus,
  WorkerStatusBanner,
  WorkerStatusIndicator,
} from "@/admin/monitoring/worker-status"
import "@/admin/monitoring/workspace.css"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import { TerminalLogo } from "@/components/terminal/terminal-logo"
import { ThemeToggle } from "@/components/terminal/theme-toggle"
import { getTerminalHeadMeta, TERMINAL_TITLE } from "@/lib/terminal/metadata"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

// Whether this browser's layout already admitted the caller as an admin. A
// navigation that stays inside the console reuses it instead of asking the
// server again; entering the console from elsewhere always re-checks. Every
// admin server function still runs `assertAdminContext()`, which remains the
// security boundary. Never set on the server, where module state is shared
// across requests.
let isAdminConfirmed = false

const TerminalAdminLayout = () => {
  const { theme } = useTerminalTheme()
  const router = useRouter()
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const workerStatus = useWorkerStatus()
  return (
    <MotionConfig reducedMotion="user">
      <div
        data-terminal-theme={theme}
        className="monitoring-workspace bg-surface-base text-text-primary min-h-screen font-mono"
      >
        <header className="bg-surface-chrome flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 lg:px-8">
          <Link
            to="/terminal/admin"
            className="flex shrink-0 items-center gap-3 rounded-sm"
          >
            <TerminalLogo size={32} theme={theme} isDecorative />
            <span className="leading-tight">
              <span className="text-muted-foreground block text-xs">
                {TERMINAL_TITLE}
              </span>
              <span className="font-semibold">Monitoring</span>
            </span>
          </Link>
          {/* Active styling keys off the `aria-current` every router Link sets,
              so a link's own muted color never fights its active color. */}
          <nav
            aria-label="Console"
            className="[&>a:hover]:text-foreground [&>a[aria-current=page]]:bg-accent [&>a[aria-current=page]]:text-foreground flex items-center gap-1 text-xs [&>a]:rounded-sm [&>a]:px-2 [&>a]:py-1.5 [&>a]:transition-colors"
          >
            <Link
              to="/terminal/admin"
              activeOptions={{ exact: true, includeSearch: false }}
              className="text-muted-foreground"
            >
              Companies
            </Link>
            <Link
              to="/terminal/admin/runs"
              activeOptions={{ includeSearch: false }}
              className="text-muted-foreground"
            >
              Runs
            </Link>
            <IdentityReviewsNavLink />
            <Link to="/terminal/admin/spend" className="text-muted-foreground">
              Spend
            </Link>
            <Link
              to="/terminal/admin/policy-tuner"
              className="text-muted-foreground"
            >
              Policy tuner
            </Link>
            <Link
              to="/terminal/admin/digest-tuner"
              className="text-muted-foreground"
            >
              Digest tuner
            </Link>
          </nav>
          <WorkerStatusIndicator {...workerStatus} />
          <div className="order-last w-full lg:order-none lg:ml-auto lg:w-auto lg:max-w-md lg:flex-1">
            <MonitoringCompanySearch />
          </div>
          <div className="ml-auto flex items-center gap-2 lg:ml-0">
            <ThemeToggle />
            <button
              className={cn(terminalControlVariants({ size: "m" }), "text-xs")}
              onClick={async () => {
                isAdminConfirmed = false
                try {
                  await terminalLogout()
                  await router.invalidate()
                  await router.navigate({ to: "/terminal/login" })
                } catch {
                  setLogoutError("Log out failed. Try again.")
                }
              }}
            >
              Log out
            </button>
          </div>
          {logoutError && (
            <p role="alert" className="text-assessment-critical text-xs">
              {logoutError}
            </p>
          )}
        </header>
        <main className="mx-auto max-w-400 space-y-6 px-4 py-8 lg:px-8">
          <WorkerStatusBanner {...workerStatus} />
          <Outlet />
        </main>
      </div>
    </MotionConfig>
  )
}
export const Route = createFileRoute("/_terminalAdmin")({
  head: () => ({
    meta: getTerminalHeadMeta(`Monitoring | ${TERMINAL_TITLE}`),
  }),
  beforeLoad: async ({ cause }) => {
    if (cause !== "enter" && isAdminConfirmed) return
    const auth = await getTerminalAuthContext()
    const isAdmin = !!auth.user && auth.userType === "admin"
    isAdminConfirmed = isAdmin && typeof document !== "undefined"
    if (!isAdmin) throw redirect({ to: "/terminal/login" })
  },
  component: TerminalAdminLayout,
})
